import { IConsultation } from './consultation.dto';
import { Prescription } from './prescription.dto';
import { IAppointment } from './appointment.dto';
import { IAddress, IPharmacyAddress } from './common.dto';

export class IPatientTokenPayload {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  uname?: string;
  provinceCode?: string;
  phone?: string;
  stripeId?: string;
  whopMemberId?: string;
  bigcommerceId?: string;
  iat?: string;
  exp?: string;
  token?: string;
  isLegacy?: boolean;
  organization?: string;
  requireNewPassword?: string;
  referrer: string;
  ip?: string;
  dob?: string;
  extras?: any;
}

export interface RingCentralFaxWebhookPayload {
  uuid: string;
  event: string;
  timestamp: string;
  subscriptionId: string;
  body: {
    id: string;
    to: Array<{
      phoneNumber: string;
      name?: string;
    }>;
    from: {
      phoneNumber: string;
      name?: string;
    };
    type: string;
    direction: string;
    faxStatus: string;
    faxResolution: string;
    faxPageCount: number;
    faxCoverPageText?: string;
    faxUrl?: string;
    faxFileType?: string;
    faxFileSize?: number;
    creationTime: string;
    readStatus: string;
    priority: string;
    attachments: Array<{
      id: string;
      uri: string;
      type: string;
      contentType: string;
      vmDuration?: number;
    }>;
    lastModifiedTime: string;
    vmTranscriptionStatus?: string;
    vmTranscriptionText?: string;
  };
}

export class TokenResponse {
  token: string;
}

export class ResetResponse {
  status: null | 'success' | 'fail';
  message?: string;
  data?: any;
}

export class PasswordReset {
  id: string;
  code: string;
  iat: string;
  exp: string;
}

export class AuthRequest {
  username: string;
  password: string;
  name?: string;
}

export class Form {
  user: Person;
  questions: Question[];
}

export class Question {
  id: string;
  description: string;
  answer: string;
  availableAnswers?: string[];
  warnings: string[];
  // TODO maybe add validation rules and answer types
}

export class Person {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  gender?: string;
}

export class UserDetails {
  _id: string;
  email: string;
  name: string;
  exp: number;
  iat: number;
}

export class IResult {
  success: boolean;
  data?: any;
}

export enum DrawerType {
  CREATE_RX = 'create-rx',
  EDIT_RX = 'edit-rx',
  DRUG_DETAIL = 'drug-detail',
  CREATE_FULFILL = 'create-fulfill',
  CREATE_CONSULTATION = 'create-consultation',
  FIND_ORDER = 'find-order',
  OPEN_CONSULTATION = 'open-consultation',
  PRESCRIPTION_PDF = 'prescription-pdf',
  OPEN_COUPON = 'open-coupon',
  OPEN_PROMOTION = 'open-promotion',
  LAB_REQ_DETAIL = 'lab-req-detail',
  LAB_REQ_DETAIL_V2 = 'lab-req-detail-v2',
  CUSTOMER_CHAT = 'customer-chat',
  SCHEDULE_EDITOR = 'schedule',
  INVOICE_PREVIEW = 'invoice-preview',
  CREATE_PATIENT = 'create-patient',
  APPOINTMENT_DETAILS = 'appointment-details',
  ORDER_CREATE = 'order-create',
  ORDER_DETAILS = 'order-details',
  SUBSCRIPTION_DETAILS = 'subscription-details',
  ORDER_UPDATE = 'order-update',
  WATCHLIST_MANAGER = 'watchlist-manager',
  CUSTOMER_ACTIVITY = 'customer-activity',
  CREATE_ACTIVITY = 'create-activity',
  CANNED_MESSAGES = 'canned-messages',
  ORDER_REFUND = 'order-refund',
  CUSTOMER_PRESCRIPTIONS = 'customer-prescriptions',
  CREATE_SUBSCRIPTION = 'create-subscription',
  VIEW_ALL_CONSULTATIONS = 'view-all-consultations'
  // ORDER_REFILL = 'order-refill'
}

export interface DrawerParms {
  name: DrawerType;
  props?: any;
  onClose?: (result?: any) => void;
}

export class IAccountProfile {
  firstName: string;
  lastName: string;
  dob: string;
  email: string;
  phone: string;
  gender: string;
}

export type IAccountAddressOther = IAddress[];
export class IAccountAddress {
  default: Partial<IAddress>;
  other?: IAccountAddressOther;
}

export class IAccountDTO {
  customerId: string;
  profile: IAccountProfile;
  prescriptions: Prescription[];
  consultations: IConsultation[];
  appointments: IAppointment[];
  availableCredit: number;
  address: IAccountAddress;
  pickupPharmacy?: IPharmacyAddress;
  pickupPharmacyId?: string;
}
export type LoadType = 'newer' | 'older' | 'new' | 'page';

export type ISelectOption = {
  value: string;
  label: string;
};

export class DisplayError implements Error {
  name: string;
  message: string;
  stack?: string;
  title: string;
  displayMessage?: string;

  constructor({
    displayMessage,
    message,
    title
  }: {
    displayMessage?: string;
    message?: string;
    title?: string;
  }) {
    this.displayMessage = displayMessage;
    this.name = 'Error';
    this.message = message;
    this.stack = '';
    this.title = title || 'System Error';
  }
}

export interface CurexaWebhookPayload {
  order_id: string;
  status: string;
  status_details: any;
  tracking_number: any;
  carrier?: string;
}

export interface LifeFileWebhookPayload {
  pharmacyLocation: string;
  fillId: string;
  rxNumber: string;
  // Depending on Lifefile API version, might be the referenceID
  foreignRxNumber: string;
  // Depending on Lifefile API version, might be the referenceID
  orderReferenceID?: string;
  // Depending on Lifefile API version, might be the referenceID
  orderReferenceId?: string;
  // Depending on Lifefile API version, might be the referenceID
  referenceId?: string; //this is our masterId
  orderId: string;
  practiceId: string;
  providerId: string;
  patientId: string;
  IfdrugId: string;
  rxStatus: string;
  rxstatus: string;
  rxStatusDateTime: string;
  deliveryService: string;
  service: string;
  trackingNumber: string;
  trackingnumber?: string;
  shipAddressLine1: string;
  shipAddressLine2: string;
  shipAddressLine3: string;
  shipCity: string;
  shipState: string;
  shipZip: string;
  shipCountry: string;
  shipCarrier: string;
}

export type ExternalPharmacySource = 'beluga' | 'dosespot';

export interface IExternalPharmacy {
  _id: string;
  label: string;
  description?: string;
  address: IPharmacyAddress;
}

export interface IExternalPharmacySearchParams {
  state: string;
  name?: string;
  zip?: string;
  address1?: string;
  address2?: string;
  city?: string;
}
